import { create } from 'zustand';
import { doc, setDoc, onSnapshot } from '@/lib/firebase/firestore';
import { db } from '@/config/firebase';
import { useAuthStore, getEffectiveUid } from './auth-store';
import { useAppStore } from './app-store';
import { useSharedAdminOrgStore, getSharedOrgContext } from './shared-admin-org-store';
import { useAppUsersStore } from './app-users-store';
import type { Team, UsersAccessPermissions, TeamMember, TeamPermissions } from '@/types/settings';
import { readScopedCache, writeScopedCache } from '@/lib/cache/scoped-cache';

const FIRESTORE_KEY = 'usersAccessPermissions';
const LS_CACHE_KEY = 'ua_cache_team_permissions' as const;

/** Reserved ID for the virtual default group that applies to all users */
export const DEFAULT_GROUP_ID = '_default';

/** Build the virtual default group object (full access everywhere) */
function buildDefaultGroup(): Team {
  return {
    id: DEFAULT_GROUP_ID,
    name: 'Default',
    description: 'Permissions that apply to all users unless overridden by a specific group',
    members: {},
    permissions: {},
    createdAt: '',
  };
}

/** Standing real-time subscription to the shared team doc, plus the doc path it
 *  is currently watching. The path embeds the effective uid (a shared user's
 *  selected admin), which resolves asynchronously during boot — so the
 *  subscription is re-pointed when that changes (see subscribeTeams). Kept live
 *  so an admin's permission edits propagate to every shared user's session
 *  without a manual reload. */
let unsubscribeTeams: (() => void) | null = null;
let subscribedPath: string | null = null;

interface TeamStore {
  teams: UsersAccessPermissions;
  selectedTeamId: string | null;
  loading: boolean;
  error: string | null;
  teamsLoaded: boolean;
  /** 'idle' | 'saving' | 'saved' | 'error' — for visual feedback on permission changes */
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';

  /** Restore teams from localStorage cache (sync, instant) */
  restoreFromCache(): void;
  /** Start (once) a live subscription to team permissions from Firestore */
  loadTeams(): Promise<void>;
  addTeam(name: string, description: string): void;
  updateTeam(teamId: string, updates: Partial<Pick<Team, 'name' | 'description'>>): void;
  deleteTeam(teamId: string): void;
  /** Adds a member and resolves their Firebase Auth UID (via fetchUserDetails).
   *  The uid is stored on the member so server-side notification routing can
   *  skip the email→uid lookup on every message. Fire-and-forget safe — callers
   *  don't need to await. */
  addMember(teamId: string, member: TeamMember): Promise<void>;
  removeMember(teamId: string, email: string): void;
  updatePermissions(teamId: string, permissions: TeamPermissions): void;
  selectTeam(teamId: string | null): void;
}

/**
 * Get the Firestore document path for shared team data.
 * Path: uaAppPreferences/{saasCode}_orgs/{orgId}/{extensionName}/shared/{uid}
 */
function getSharedDocPath(): string | null {
  const uid = getEffectiveUid();
  const extensionName = useAppStore.getState().extensionName;
  // KAN-178: for a shared user this is the ADMIN's org — the doc the admin
  // writes and the backend reads — not the user's own CRM org.
  const ctx = getSharedOrgContext();

  if (!uid || !extensionName || !ctx) return null;

  return `uaAppPreferences/${ctx.saasCode}_orgs/${ctx.orgId}/${extensionName}/shared/${uid}`;
}

/** Auto-clear timer for saved status */
let savedTimer: ReturnType<typeof setTimeout> | null = null;

export const useTeamStore = create<TeamStore>((set, get) => ({
  teams: {},
  selectedTeamId: null,
  loading: false,
  error: null,
  teamsLoaded: false,
  saveStatus: 'idle',

  restoreFromCache() {
    try {
      const cached = readScopedCache(LS_CACHE_KEY);
      if (!cached) return;
      const teams = JSON.parse(cached) as UsersAccessPermissions;
      if (!teams[DEFAULT_GROUP_ID]) {
        teams[DEFAULT_GROUP_ID] = buildDefaultGroup();
      }
      set({ teams, teamsLoaded: true });
      console.log('[Teams] Restored from localStorage cache');
    } catch { /* corrupt cache, ignore */ }
  },

  async loadTeams() {
    subscribeTeams();
  },

  addTeam(name, description) {
    const id = name.trim().toLowerCase().replace(/\s+/g, '_');
    const newTeam: Team = {
      id,
      name: name.trim(),
      description: description.trim(),
      members: {},
      permissions: {},
      createdAt: new Date().toISOString(),
    };
    const updated = { ...get().teams, [id]: newTeam };
    set({ teams: updated, selectedTeamId: id });
    persistWithStatus(set, updated);
  },

  updateTeam(teamId, updates) {
    // Prevent renaming or editing the virtual default group metadata
    if (teamId === DEFAULT_GROUP_ID) return;
    const teams = { ...get().teams };
    if (!teams[teamId]) return;
    teams[teamId] = { ...teams[teamId], ...updates };
    set({ teams });
    persistWithStatus(set, teams);
  },

  deleteTeam(teamId) {
    // Prevent deleting the virtual default group
    if (teamId === DEFAULT_GROUP_ID) return;
    const teams = { ...get().teams };
    delete teams[teamId];
    const selectedTeamId = get().selectedTeamId === teamId ? null : get().selectedTeamId;
    set({ teams, selectedTeamId });
    persistWithStatus(set, teams);
  },

  async addMember(teamId, member) {
    // Default group has no members — it applies to everyone
    if (teamId === DEFAULT_GROUP_ID) return;
    const teams = { ...get().teams };
    if (!teams[teamId]) return;

    // Optimistic insert so the UI updates immediately.
    teams[teamId] = {
      ...teams[teamId],
      members: { ...teams[teamId].members, [member.email]: member },
    };
    set({ teams });
    persistWithStatus(set, teams);

    // Resolve uid in the background if the caller didn't supply one.
    // fetchUserDetails is cached (Zustand + IDB 24h), so this is effectively
    // a one-time cost per member. Once resolved we re-persist with the uid
    // attached so server-side routing can skip email→uid lookups.
    if (member.uid) return;
    try {
      const details = await useAppUsersStore.getState().fetchUserDetails(member.email);
      if (!details?.uid) return;
      const latest = { ...get().teams };
      if (!latest[teamId]) return;
      const existing = latest[teamId].members[member.email];
      if (!existing || existing.uid === details.uid) return;
      latest[teamId] = {
        ...latest[teamId],
        members: {
          ...latest[teamId].members,
          [member.email]: { ...existing, uid: details.uid },
        },
      };
      set({ teams: latest });
      persistWithStatus(set, latest);
    } catch (err) {
      console.warn('[Teams] uid resolution failed for', member.email, err);
      // Non-fatal — the member is still saved (without uid). The backend has
      // its own cached email→uid fallback at dispatch time.
    }
  },

  removeMember(teamId, email) {
    if (teamId === DEFAULT_GROUP_ID) return;
    const teams = { ...get().teams };
    if (!teams[teamId]) return;
    const members = { ...teams[teamId].members };
    delete members[email];
    teams[teamId] = { ...teams[teamId], members };
    set({ teams });
    persistWithStatus(set, teams);
  },

  updatePermissions(teamId, permissions) {
    const teams = { ...get().teams };
    if (!teams[teamId]) return;
    teams[teamId] = { ...teams[teamId], permissions };
    set({ teams });
    persistWithStatus(set, teams);
  },

  selectTeam(teamId) {
    set({ selectedTeamId: teamId });
  },
}));

/**
 * (Re)point the standing team subscription at the CURRENT shared doc.
 *
 * The doc path embeds the effective uid — for a shared user that's their
 * selected admin, which resolves asynchronously after boot. A listener started
 * before it resolves would watch the wrong (empty) doc, and because it never
 * re-subscribed, permission edits (e.g. chat-label levels) would never reach
 * the label dropdown. Re-invoking when the effective uid changes re-points it.
 * Idempotent: a no-op while already watching the correct doc.
 */
function subscribeTeams(): void {
  const docPath = getSharedDocPath();
  if (!docPath) {
    // No context (app not ready, or signed out) — drop any stale subscription
    // so a no-reload sign-out doesn't keep watching the previous user's doc.
    if (unsubscribeTeams) { unsubscribeTeams(); unsubscribeTeams = null; }
    subscribedPath = null;
    return;
  }
  if (unsubscribeTeams && subscribedPath === docPath) return; // already correct
  if (unsubscribeTeams) { unsubscribeTeams(); unsubscribeTeams = null; }
  subscribedPath = docPath;
  console.log('[Teams] subscribing:', docPath);

  const set = useTeamStore.setState;
  set({ loading: true, error: null });
  // Live subscription (not a one-shot read): permissions are shared across
  // sessions, so an admin editing a team must reach every shared user's live
  // session without a reload. `hasPendingWrites` is our own not-yet-committed
  // optimistic write echoing back — skip it so rapid edits aren't clobbered.
  unsubscribeTeams = onSnapshot(
    doc(db, docPath),
    (snap) => {
      if (snap.metadata.hasPendingWrites) return;
      const data = snap.exists() ? snap.data() : {};
      const teams = (data[FIRESTORE_KEY] || {}) as UsersAccessPermissions;
      if (!teams[DEFAULT_GROUP_ID]) {
        teams[DEFAULT_GROUP_ID] = buildDefaultGroup();
      }
      set({ teams, teamsLoaded: true, loading: false });
      // Cache to localStorage for instant restore on next page load
      try { writeScopedCache(LS_CACHE_KEY, JSON.stringify(teams)); } catch { /* */ }
    },
    (err) => {
      console.error('[Teams] team subscription error:', err);
      set({ error: (err as Error).message, loading: false, teamsLoaded: true });
    }
  );
}

// The shared doc path depends on the effective uid (selected admin /
// impersonation), which resolves asynchronously during boot and can change
// in-session. Re-point the team subscription on any auth change — subscribeTeams
// is a no-op unless the doc path actually changed — so a listener that started
// on the wrong/empty doc gets corrected and permission edits reflect live.
useAuthStore.subscribe(() => { subscribeTeams(); });
// The admin's org resolves asynchronously for shared users (KAN-178) — re-point
// the subscription when it lands so permissions come from the admin's doc.
useSharedAdminOrgStore.subscribe(() => { subscribeTeams(); });

/** Persist to localStorage + Firestore with saving/saved/error status */
function persistWithStatus(
  set: (partial: Partial<TeamStore>) => void,
  teams: UsersAccessPermissions,
) {
  // localStorage — instant
  try { writeScopedCache(LS_CACHE_KEY, JSON.stringify(teams)); } catch { /* */ }

  // Clear any pending "saved" auto-clear timer
  if (savedTimer) { clearTimeout(savedTimer); savedTimer = null; }
  set({ saveStatus: 'saving' });

  const docPath = getSharedDocPath();
  if (!docPath) { set({ saveStatus: 'idle' }); return; }

  const sanitized = JSON.parse(JSON.stringify(teams));
  const docRef = doc(db, docPath);
  setDoc(docRef, { [FIRESTORE_KEY]: sanitized }, { mergeFields: [FIRESTORE_KEY] })
    .then(() => {
      set({ saveStatus: 'saved' });
      savedTimer = setTimeout(() => set({ saveStatus: 'idle' }), 2000);
    })
    .catch((error) => {
      console.error('[Teams] Failed to persist:', error);
      set({ saveStatus: 'error' });
      savedTimer = setTimeout(() => set({ saveStatus: 'idle' }), 3000);
    });
}
